/* eslint-disable react-refresh/only-export-components */
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type BusinessTerminology = {
  customer: {
    singular: string;
    plural: string;
  };
  company: {
    singular: string;
    plural: string;
  };
};

export const DEFAULT_TERMINOLOGY: BusinessTerminology = {
  customer: { singular: "Customer", plural: "Customers" },
  company: { singular: "Company", plural: "Companies" },
};

type TerminologyContextValue = {
  terminology: BusinessTerminology;
  customer: string;
  customers: string;
  company: string;
  companies: string;
  isLoading: boolean;
  canEdit: boolean;
  saveTerminology: (terms: BusinessTerminology) => Promise<void>;
  resetTerminology: () => Promise<void>;
};

const TerminologyContext = createContext<TerminologyContextValue | undefined>(undefined);

const cleanTerm = (value: unknown, fallback: string) => {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 40 ? trimmed : fallback;
};

const parseTerminology = (value: unknown): BusinessTerminology => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return DEFAULT_TERMINOLOGY;
  const source = value as { customer?: unknown; company?: unknown };

  const parsePair = <Key extends keyof BusinessTerminology>(
    key: Key,
  ): BusinessTerminology[Key] => {
    const pair = source[key];
    if (!pair || typeof pair !== "object" || Array.isArray(pair)) {
      return DEFAULT_TERMINOLOGY[key];
    }

    return {
      singular: cleanTerm(
        (pair as { singular?: unknown }).singular,
        DEFAULT_TERMINOLOGY[key].singular,
      ),
      plural: cleanTerm(
        (pair as { plural?: unknown }).plural,
        DEFAULT_TERMINOLOGY[key].plural,
      ),
    };
  };

  return {
    customer: parsePair("customer"),
    company: parsePair("company"),
  };
};

export const TerminologyProvider = ({ children }: { children: ReactNode }) => {
  const { businessId, isStaff } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["business-terminology", businessId] as const;

  const terminologyQuery = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return DEFAULT_TERMINOLOGY;
      const { data, error } = await supabase
        .from("business_terminology")
        .select("terms")
        .eq("business_id", businessId)
        .maybeSingle();
      if (error) throw error;
      return parseTerminology(data?.terms);
    },
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: async (terms: BusinessTerminology) => {
      if (!businessId) throw new Error("No active business was found.");
      const next = parseTerminology(terms);
      const { error } = await supabase
        .from("business_terminology")
        .upsert(
          { business_id: businessId, terms: next },
          { onConflict: "business_id" },
        );
      if (error) throw new Error(error.message);
      return next;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
    },
  });

  const value = useMemo<TerminologyContextValue>(() => {
    const terminology = terminologyQuery.data ?? DEFAULT_TERMINOLOGY;
    return {
      terminology,
      customer: terminology.customer.singular,
      customers: terminology.customer.plural,
      company: terminology.company.singular,
      companies: terminology.company.plural,
      isLoading: terminologyQuery.isLoading,
      canEdit: !!businessId && !isStaff,
      saveTerminology: async (terms) => {
        await saveMutation.mutateAsync(terms);
      },
      resetTerminology: async () => {
        await saveMutation.mutateAsync(DEFAULT_TERMINOLOGY);
      },
    };
  }, [
    isStaff,
    businessId,
    saveMutation,
    terminologyQuery.data,
    terminologyQuery.isLoading,
  ]);

  return <TerminologyContext.Provider value={value}>{children}</TerminologyContext.Provider>;
};

export const useTerminology = () => {
  const context = useContext(TerminologyContext);
  if (!context) throw new Error("useTerminology must be used within a TerminologyProvider");
  return context;
};
